/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { observer } from "mobx-react-lite";
import { FC, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router";
import { useMediaQuery } from "react-responsive";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import { uniqueId } from "lodash";
import { toJS } from "mobx";
import "react-image-lightbox/style.css";

// Компоненты
import { Spinner } from "../../components/ui/Spinner";
import Button from "../../components/ui/Button";
import { Select } from "../../components/ui/Select";
import Modal from "../../components/ui/Modal";
import Counter from "../../components/ui/Form/FormCounter";
import ProductCardCarousel from "../../components/ProductCardCarousel";

// Сторы и утилиты
import productStore from "./store/productStore";
import basketStore from "../BasketPage/store/basketStore";
import appStore from "../../stores/appStore";
import { baseURL } from "../../libs/http";
import { getDeclension, getProductCountDescription } from "../../utils/tools";
import { Product } from "../../types/product";
import backArrow from "../../assets/back_arrow.svg";
import favoriteIcon from "../../assets/favorite-icon.svg";
import { Helmet } from "react-helmet-async";
import accountStore from "../AccountPage/store/accountStore";
import MobileSearchBar from "../../components/MobileSearchBar";

type FormData = {
  warehouse: string;
  quantity: number;
};

const DEFAULT_IMAGE =
  "https://avatars.mds.yandex.net/i?id=8e294c809ff68fd77490c5cab8f9a29e_l-4276653-images-thumbs&n=13";

const ProductPage: FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { refKey } = useParams();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Состояния
  const [selectedBuy, setSelectedBuy] = useState(false);
  const [imageUrl, setImageUrl] = useState(`${baseURL}/image/${refKey}`);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isToggleFavorite, setIsToggleFavorite] = useState(false);

  // Форма
  // @ts-ignore
  const { handleSubmit, watch, setValue, control } = useForm<FormData>({
    defaultValues: {
      quantity: 1,
      warehouse: "",
    },
  });

  const selectedWarehouse = watch("warehouse");
  const quantity = watch("quantity", 1);

  // Мемоизированные значения
  const mobileStyles = useMemo(
    () => ({
      container: isMobile ? "p-0" : "p-[30px]",
      productCard: isMobile
        ? "flex-col px-[15px] pb-[15px] pt-0 gap-4 rounded-none shadow-none bg-transparent w-full"
        : "w-[1380px] h-[480px] flex-row p-[30px] gap-[30px] rounded-[20px] shadow-lg [box-shadow:0_2px_20px_0_#0000001A] [-webkit-box-shadow:0_2px_20px_0_#0000001A] bg-white",
      image: isMobile
        ? "w-[100vw] min-h-[auto] max-h-[268px] self-center object-contain cursor-zoom-in"
        : "w-[570px] object-contain rounded-[15px] cursor-zoom-in",
      descriptionBlock: isMobile
        ? "flex-col gap-4 bg-transparent shadow-none rounded-none"
        : "flex-row gap-4 bg-white shadow-lg [box-shadow:0_2px_20px_0_#0000001A] [-webkit-box-shadow:0_2px_20px_0_#0000001A] rounded-2xl",
      price: isMobile
        ? "text-xl"
        : "text-[#232323] text-[30px] tracking-[-0.02em] ",

        favoriteIcon: !isMobile ? "!border-[2px] min-w-[53px] min-h-[51px] !rounded-[15px] !w-[61px] !h-[61px] !p-0 flex items-center justify-center transition-all duration-200 ":
        "`!border-[2px] min-w-[61px] min-h-[61px] !rounded-[15px] !w-[61px] !h-[51px] !p-0 flex items-center justify-center transition-all duration-200 "
    }),
    [isMobile]
  );

  // Эффекты
  useEffect(() => {
    productStore.getProductByKey(refKey || "");
  }, [refKey]);

  useEffect(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const newImageUrl =
      productStore.product?.image_key === "00000000-0000-0000-0000-000000000000"
        ? DEFAULT_IMAGE
        : `${baseURL}/image/${refKey}`;

    setImageUrl(newImageUrl);

    return () => controller.abort();
  }, [refKey, productStore.product?.image_key]);


const [inFavoriteExists, setInFavoriteExists] = useState(false);

useEffect(() => {
  if (!appStore.isLoginIn) {
    const exists = JSON.parse(localStorage.getItem("favorites") || "[]").some(
      (i: Product) => i.ref_key === productStore.product?.ref_key
    );
    setInFavoriteExists(exists);
  } else {
    setInFavoriteExists(!!productStore.product?.is_favorite);
  }
}, [
  productStore.product?.ref_key,
  productStore.isLoading,
  productStore.product?.is_favorite,
  appStore.isLoginIn,
  JSON.parse(localStorage.getItem("favorites") || "[]")
]);

  // Обработчики
  const getAvailableQuantity = useCallback(() => {
    if (!selectedWarehouse || !productStore.availavleCurrentCity) return 0;
    const warehouse = productStore.availavleCurrentCity.find(
      (item) => item.Stock === selectedWarehouse
    );
    return warehouse?.qnt ?? 0;
  }, [selectedWarehouse]);

  const handleQuantityChange = useCallback(
    (value: number) => {
      const availableQty = getAvailableQuantity();
      if (!selectedWarehouse) {
        toast.info(`Сначала выберете магазин!`, {
          autoClose: 300,
        });
        return;
      }
      if (value > availableQty) {
        value = availableQty;
        toast.info(`Максимальное доступное количество: ${availableQty}`, {
          autoClose: 300,
        });
      }
      setValue("quantity", value);
    },
    [getAvailableQuantity, selectedWarehouse, setValue]
  );

  useEffect(() => {
    const availableQty = getAvailableQuantity();
    if (quantity > availableQty) {
      setValue("quantity", availableQty);
      toast.info(`Максимальное доступное количество: ${availableQty}`, {
        autoClose: 300,
      });
    }
  }, [selectedWarehouse, quantity, getAvailableQuantity, setValue]);

  const handleImageError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      event.currentTarget.onerror = null;
      event.currentTarget.src = DEFAULT_IMAGE;
    },
    []
  );

  const onCloseModal = useCallback(() => {
    setSelectedBuy(false);
  }, []);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!productStore.product) return;

      basketStore.addToBasket({
        product: toJS(productStore.product),
        warehouse: data.warehouse,
        quantity: data.quantity,
        id: uniqueId("basket_"),
      });

      toast.success("Товар добавлен в корзину", {
        autoClose: 300,
      });
      setSelectedBuy(false);
    },
    [productStore.product]
  );

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Рендер функции
  const renderStoreAvailability = useCallback(() => {
    if (productStore.availavleCurrentCity?.length === 0) {
      return (
        <>
          <span className="text-[16px] text-[#474A51] tracking-[-0.02em] mb-[30px]">
            Товара нет в наличии в выбранном городе{" "}
            {productStore.totalQntAnotherCity > 0 &&
              ", но он есть в другом городе. Для заказа свяжитесь с менеджером точки в вашем городе, в которой хотите получить заказ."}
          </span>
          <div className="flex flex-col gap-[10px]">
            {productStore.totalQntAnotherCity > 0 &&
              productStore.availavleAnotherCity?.map((i) => (
                <div
                  key={i.Stock}
                  className={
                    !isMobile
                      ? "flex flex-row text-[18px] text-[#474A51] font-semibold"
                      : "flex flex-row text-[14px] text-[#474A51] font-semibold"
                  }
                >
                  <span className="text-nowrap whitespace-nowrap">
                    {i.Stock}
                  </span>
                  <div className="flex-1 w-full border-b border-dotted ml-[3px] mr-[3px]" />
                  <span className="text-nowrap whitespace-nowrap">
                    {getProductCountDescription(i.qnt)}
                  </span>
                </div>
              ))}
          </div>
        </>
      );
    }

    return (
      <div className="flex flex-col gap-[10px]">
        {productStore.availavleCurrentCity?.map((i) => (
          <div
            key={i.Stock}
            className={
              !isMobile
                ? "flex flex-row text-[18px] text-[#474A51] font-semibold"
                : "flex flex-row text-[14px] text-[#474A51] font-semibold"
            }
          >
            <span className="text-nowrap whitespace-nowrap">{i.Stock}</span>
            <div className="flex-1 border-b border-dotted ml-[3px] mr-[3px]" />
            <span className="text-nowrap whitespace-nowrap">
              {getProductCountDescription(i.qnt)}
            </span>
          </div>
        ))}
      </div>
    );
  }, []);

  const renderAnalogProducts = useCallback(() => {
    if (!(productStore?.product?.analog_array as Product[])?.length)
      return null;

    return (
      <div className={!isMobile ? "w-[1380px]" : ""}>
        <h2
          className={
            isMobile
              ? "text-[20px] pl-[15px] text-[#232323] tracking-[-0.02em] font-bold"
              : "text-[27px] font-bold mb-[25px]"
          }
        >
          Также может подойти
        </h2>
        <ProductCardCarousel
          isMobile={isMobile}
          sliderClassName={
            isMobile
              ? "bg-transparent rounded-[20px] w-full !h-full"
              : "bg-white shadow-lg [box-shadow:0_2px_20px_0_#0000001A] [-webkit-box-shadow:0_2px_20px_0_#0000001A] rounded-[20px] w-full p-1 h-full"
          }
          products={(productStore.product?.analog_array as Product[]) || []}
          mobileSpaceBetween={12}
          desktopSpaceBetween={0}
          mobileSlideClassName="!w-[200px] !h-auto"
          desktopSlideClassName="!w-[246px] !h-auto"
          mobileCardClassName="h-full"
          desktopCardClassName="border-r-1 border-r-[#54545657] h-full"
        />
      </div>
    );
  }, [isMobile, productStore.product?.analog_array]);

  if (productStore.isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const renderBadge = () => {
    if (!isMobile) {
      if (productStore.availavleCurrentCity?.length >= 1) {
        return (
          <div className="bg-[#29A2394D] text-[#29A239] text-[15px] rounded-[35px] px-[14px] py-[7px]">
            В наличии
          </div>
        );
      } else if (
        productStore.product?.available.has_stock &&
        productStore.availavleCurrentCity?.length < 1
      ) {
        return (
          <div className="bg-[#FFC1644D] text-[#E1800A] text-[15px] rounded-[35px] px-[14px] py-[7px]">
            В другом городе
          </div>
        );
      } else {
        return (
          <div className="bg-[#474A514D] text-[#555555] text-[15px] rounded-[35px] px-[14px] py-[7px]">
            Нет в наличии
          </div>
        );
      }
    } else {
      if (productStore.availavleCurrentCity?.length >= 1) {
        return (
          <div className="bg-[#29A2394D] text-[#29A239] text-[13px] rounded-[35px] px-[10px] py-[5px]">
            В наличии
          </div>
        );
      } else if (
        productStore.product?.available.has_stock &&
        productStore.availavleCurrentCity?.length < 1
      ) {
        return (
          <div className="bg-[#FFC1644D] text-[#E1800A] text-[13px] rounded-[35px] px-[10px] py-[5px]">
            В другом городе
          </div>
        );
      } else {
        return (
          <div className="bg-[#474A514D] text-[#555555] text-[13px] rounded-[35px] px-[10px] py-[5px]">
            Нет в наличии
          </div>
        );
      }
    }
  };

  return (
    <div
      className={`flex flex-col ${
        isMobile ? "pt-4 gap-4" : "gap-[30px]"
      } ${mobileStyles.container}`}
    >
      <Helmet>
        {productStore?.product?.name && (
          <>
            <meta
              name="description"
              content={`${String(productStore?.product?.description)} купить`}
            ></meta>
            <title>
              КЦ Авто | {String(productStore?.product?.name ?? "Загрузка")}
            </title>
            <meta
              name="keywords"
              content={`Корея центр, автозапчасти купить, КЦАвто, КЦ авто, интернет магазин автозапчастей`}
            ></meta>
          </>
        )}
      </Helmet>
      {isMobile && <MobileSearchBar className="w-full px-[15px]" />}
      {/* Fullscreen Image Overlay */}
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={toggleFullscreen}
        >
          <div className="relative max-w-full max-h-full">
            <img
              src={imageUrl}
              className="max-w-full max-h-screen object-contain"
              alt={productStore.product?.name}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute top-4 right-4 text-white text-2xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center"
              onClick={toggleFullscreen}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Product Card */}
      <div className={`flex ${mobileStyles.productCard}`}>
        {isMobile && (
          <div className="flex w-full items-center justify-between">
            <a
              onClick={() => history.back()}
              className="cursor-pointer flex flex-row gap-[5px] items-center font-semibold text-[16px] text-[#383181]"
            >
              <img src={backArrow} alt="" /> <span>Назад</span>
            </a>
            <div className="flex flex-row items-center gap-[8px]">
              {renderBadge()}
            </div>
          </div>
        )}
        <img
          src={imageUrl}
          className={`${mobileStyles.image}`}
          onError={handleImageError}
          alt={productStore.product?.name}
          loading="lazy"
          onClick={toggleFullscreen}
        />

        <div className="flex flex-col flex-1 ">
          {/* бэйдж в наличии */}
          {!isMobile && (
            <div className="flex flex-row mb-[40px]">{renderBadge()}</div>
          )}

          {/* end badge в наличии */}
          {/* brand name */}
          <span
            className={
              !isMobile
                ? "text-[24px] font-[550] text-[#474A51] tracking-[-0.02em] mb-[15px]"
                : "text-[20px] font-[550] text-[#474A51] mb-[10px]"
            }
          >
            {productStore.product?.brand}
          </span>
          {/* end brand name */}
          <div className="flex flex-col">
            <h2
              className={
                !isMobile
                  ? "font-[550] text-[30px] leading-[100%] text-[#232323] tracking-[-0.02em] mb-[15px]"
                  : "font-bold text-[24px] leading-[100%] text-[#232323] tracking-[-0.02em] mb-[15px]"
              }
            >
              {productStore.product?.name}
            </h2>
            <span className="font-[550] text-[17px] text-[#474A51] tracking-[-0.02em] mb-[15px]">
              Артикул: <span>{productStore.product?.artikul}</span>
            </span>
          </div>

          <div
            className={!isMobile ? "flex flex-col mt-[10px]" : "flex flex-col "}
          >
            <h2
              className={
                !isMobile
                  ? `font-bold text-[30px] mb-[8px] h-[36px] align-bottom align-text-bottom`
                  : "font-bold text-[27px] text-[#232323] align-bottom align-text-bottom"
              }
            >
              ₽{" "}
              {productStore.product?.available?.cost &&
              productStore.product?.available?.cost > 0
                ? productStore.product?.available?.cost
                : productStore.product?.price}
            </h2>

            {productStore.availavleCurrentCity?.length === 0 ? (
              <span className="text-[17px] text-red-500">
                Сейчас нет в наличии в выбранном городе
              </span>
            ) : productStore.availavleCurrentCity?.length >= 1 ? (
              <span className="text-[17px] font-medium tracking-[-0.02em]">
                В наличии в&nbsp;
                <span className="text-[#007AFF]">
                  {getDeclension(productStore.availavleCurrentCity.length, [
                    "магазине",
                    "магазинах",
                    "магазинах",
                  ])}
                </span>
              </span>
            ) : null}
          </div>

          <div className="flex flex-row mt-[30px] gap-[15px]">
            <Button
              style="primary"
              onClick={() => setSelectedBuy(true)}
              fullWidth={isMobile}
              className={!isMobile ? "h-[61px] !cursor-pointer gap-[5px] w-[231px] !rounded-[15px] px-[50px] py-[15px] font-semibold !text-[20px] flex flex-row items-center justify-center"
                : "h-[61px] !cursor-pointer gap-[5px] w-full !rounded-[15px] px-[50px] py-[15px] font-semibold !text-[20px] flex flex-row items-center justify-center"
              }
            >
              <svg
                width="30"
                height="31"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.4443 5.53223C15.1963 5.53223 15.7705 5.72819 16.167 6.12012C16.5635 6.50749 16.7617 7.07715 16.7617 7.8291V14.8838C16.7617 15.6357 16.5703 16.2054 16.1875 16.5928C15.8047 16.9847 15.2715 17.1807 14.5879 17.1807H8.87988C9.02116 16.9574 9.13965 16.7158 9.23535 16.4561C9.33105 16.2008 9.39714 15.9365 9.43359 15.6631H14.458C14.6859 15.6631 14.8727 15.5856 15.0186 15.4307C15.1644 15.2803 15.2373 15.0479 15.2373 14.7334V7.97949C15.2373 7.66504 15.1576 7.43262 14.998 7.28223C14.8385 7.12728 14.6107 7.0498 14.3145 7.0498H6.67871C6.37793 7.0498 6.14779 7.12728 5.98828 7.28223C5.83333 7.43262 5.75586 7.66504 5.75586 7.97949V10.3721C5.63281 10.3538 5.50749 10.3379 5.37988 10.3242C5.25228 10.306 5.1224 10.2969 4.99023 10.2969C4.86263 10.2969 4.73503 10.3037 4.60742 10.3174C4.48438 10.3311 4.36133 10.3493 4.23828 10.3721V7.8291C4.23828 7.07715 4.43424 6.50749 4.82617 6.12012C5.22266 5.72819 5.79915 5.53223 6.55566 5.53223H14.4443ZM7.56738 5.83984C7.56738 5.28841 7.69499 4.79622 7.9502 4.36328C8.2054 3.92578 8.55176 3.58171 8.98926 3.33105C9.43132 3.07585 9.93262 2.94824 10.4932 2.94824C11.0583 2.94824 11.5618 3.07585 12.0039 3.33105C12.446 3.58171 12.7946 3.92578 13.0498 4.36328C13.305 4.79622 13.4326 5.28841 13.4326 5.83984L11.9902 5.84668C11.9902 5.53678 11.9264 5.26335 11.7988 5.02637C11.6712 4.78483 11.4958 4.5957 11.2725 4.45898C11.0492 4.32227 10.7894 4.25391 10.4932 4.25391C10.2015 4.25391 9.94401 4.32227 9.7207 4.45898C9.4974 4.5957 9.32194 4.78483 9.19434 5.02637C9.07129 5.26335 9.00977 5.53678 9.00977 5.84668L7.56738 5.83984ZM5.00391 18.3975C4.51628 18.3975 4.05827 18.304 3.62988 18.1172C3.2015 17.9303 2.82324 17.6729 2.49512 17.3447C2.17155 17.0212 1.91634 16.6452 1.72949 16.2168C1.54264 15.7884 1.44922 15.3304 1.44922 14.8428C1.44922 14.3506 1.54264 13.8926 1.72949 13.4688C1.91634 13.0404 2.17155 12.6621 2.49512 12.334C2.82324 12.0059 3.2015 11.7507 3.62988 11.5684C4.05827 11.3815 4.51628 11.2881 5.00391 11.2881C5.49154 11.2881 5.94954 11.3815 6.37793 11.5684C6.80632 11.7507 7.18457 12.0059 7.5127 12.334C7.84082 12.6576 8.09603 13.0335 8.27832 13.4619C8.46061 13.8903 8.55176 14.3506 8.55176 14.8428C8.55176 15.3258 8.45833 15.7816 8.27148 16.21C8.08919 16.6383 7.83398 17.0166 7.50586 17.3447C7.17773 17.6729 6.79948 17.9303 6.37109 18.1172C5.94271 18.304 5.48698 18.3975 5.00391 18.3975ZM5.00391 17.1123C5.1543 17.1123 5.27507 17.0667 5.36621 16.9756C5.45736 16.8844 5.50293 16.7637 5.50293 16.6133V15.3418H6.77441C6.92025 15.3418 7.03874 15.2962 7.12988 15.2051C7.22559 15.1139 7.27344 14.9932 7.27344 14.8428C7.27344 14.6924 7.22559 14.5716 7.12988 14.4805C7.03874 14.3893 6.92025 14.3438 6.77441 14.3438H5.50293V13.0791C5.50293 12.9287 5.45736 12.8079 5.36621 12.7168C5.27507 12.6257 5.1543 12.5801 5.00391 12.5801C4.85352 12.5801 4.73275 12.6257 4.6416 12.7168C4.55046 12.8079 4.50488 12.9287 4.50488 13.0791V14.3438H3.2334C3.08301 14.3438 2.96224 14.3893 2.87109 14.4805C2.77995 14.5716 2.73438 14.6924 2.73438 14.8428C2.73438 14.9932 2.77995 15.1139 2.87109 15.2051C2.9668 15.2962 3.08757 15.3418 3.2334 15.3418H4.50488V16.6133C4.50488 16.7637 4.55046 16.8844 4.6416 16.9756C4.73275 17.0667 4.85352 17.1123 5.00391 17.1123Z"
                  fill="white"
                />
              </svg>
              <span>В корзину</span>
            </Button>
         <Button
              disabled={isToggleFavorite}
              style="outline"
              className={`${mobileStyles.favoriteIcon} ${
                inFavoriteExists
                  ? "!bg-white !border-[#383181] hover:!bg-[#0000001A] active:!bg-[#ebebeb]"
                  : "!bg-white !border-[#000000] hover:border-[#383181] hover:!bg-[#0000001A] active:!bg-[#ebebeb]"
              }`}
              onClick={async () => {
                setIsToggleFavorite(true);
                if (productStore.product) {
                   await accountStore.toggleFavorite(productStore.product);
                   if(appStore.isLoginIn) {
                    await productStore.getProductByKey(refKey || "", true);
                   }
                }
                setIsToggleFavorite(false);
              }}
            >
              <span
                className="text-[23px] font-[510] text-[#383181] leading-none"
                style={{
                  fontFamily:
                    "SF Pro, -apple-system, system-ui, BlinkMacSystemFont, sans-serif",
                }}
              >
                {isToggleFavorite ? (
                  <Spinner size="sm" />
                ) : inFavoriteExists ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="23"
                    height="22"
                    viewBox="0 0 23 22"
                    fill="none"
                  >
                    <path
                      d="M11.3423 21.0234C11.2075 21.0234 11.0428 20.9785 10.8481 20.8887C10.6535 20.7988 10.4738 20.6978 10.3091 20.5854C8.21273 19.2378 6.38965 17.8228 4.83984 16.3403C3.29753 14.8579 2.10335 13.3306 1.25732 11.7583C0.418783 10.186 -0.000488281 8.60254 -0.000488281 7.00781C-0.000488281 5.97461 0.164225 5.03125 0.493652 4.17773C0.823079 3.31673 1.28353 2.57552 1.875 1.9541C2.46647 1.33268 3.14779 0.853516 3.91895 0.516602C4.69759 0.172201 5.53613 0 6.43457 0C7.55013 0 8.52344 0.284505 9.35449 0.853516C10.1855 1.42253 10.8481 2.16374 11.3423 3.07715C11.8514 2.15625 12.5177 1.41504 13.3413 0.853516C14.1724 0.284505 15.1457 0 16.2612 0C17.1597 0 17.9982 0.172201 18.7769 0.516602C19.5555 0.853516 20.2368 1.33268 20.8208 1.9541C21.4048 2.57552 21.8615 3.31673 22.1909 4.17773C22.5278 5.03125 22.6963 5.97461 22.6963 7.00781C22.6963 8.60254 22.2733 10.186 21.4272 11.7583C20.5812 13.3306 19.387 14.8579 17.8447 16.3403C16.3099 17.8228 14.4943 19.2378 12.3979 20.5854C12.2257 20.6978 12.0423 20.7988 11.8477 20.8887C11.6605 20.9785 11.492 21.0234 11.3423 21.0234Z"
                      fill="#383181"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="23"
                    height="22"
                    viewBox="0 0 23 22"
                    fill="none"
                  >
                    <path
                      d="M-0.000488281 7.00781C-0.000488281 5.97461 0.164225 5.03125 0.493652 4.17773C0.823079 3.31673 1.28353 2.57552 1.875 1.9541C2.47396 1.33268 3.17025 0.853516 3.96387 0.516602C4.75749 0.172201 5.611 0 6.52441 0C7.59505 0 8.54215 0.228353 9.36572 0.685059C10.1893 1.13428 10.8481 1.72949 11.3423 2.4707C11.8514 1.72949 12.514 1.13428 13.3301 0.685059C14.1536 0.228353 15.1007 0 16.1714 0C17.0923 0 17.9458 0.172201 18.7319 0.516602C19.5256 0.853516 20.2181 1.33268 20.8096 1.9541C21.401 2.57552 21.8615 3.31673 22.1909 4.17773C22.5278 5.03125 22.6963 5.97461 22.6963 7.00781C22.6963 8.60254 22.2733 10.186 21.4272 11.7583C20.5812 13.3306 19.387 14.8579 17.8447 16.3403C16.3099 17.8228 14.4943 19.2378 12.3979 20.5854C12.2257 20.6978 12.0423 20.7988 11.8477 20.8887C11.6605 20.9785 11.492 21.0234 11.3423 21.0234C11.2075 21.0234 11.0428 20.9785 10.8481 20.8887C10.6535 20.7988 10.4738 20.6978 10.3091 20.5854C8.21273 19.2378 6.38965 17.8228 4.83984 16.3403C3.29753 14.8579 2.10335 13.3306 1.25732 11.7583C0.418783 10.186 -0.000488281 8.60254 -0.000488281 7.00781ZM2.26807 6.99658C2.26807 7.98486 2.50391 8.99186 2.97559 10.0176C3.45475 11.0433 4.10612 12.0578 4.92969 13.061C5.75326 14.0643 6.69661 15.0264 7.75977 15.9473C8.8304 16.8607 9.95719 17.7104 11.1401 18.4966C11.245 18.5789 11.3123 18.6201 11.3423 18.6201C11.3722 18.6201 11.4471 18.5789 11.5669 18.4966C12.7498 17.7104 13.8729 16.8607 14.936 15.9473C15.9992 15.0264 16.9425 14.0643 17.7661 13.061C18.5897 12.0578 19.2373 11.0433 19.709 10.0176C20.1882 8.99186 20.4277 7.98486 20.4277 6.99658C20.4277 6.04574 20.2368 5.21468 19.855 4.50342C19.4731 3.79215 18.9491 3.23812 18.2827 2.84131C17.6239 2.43701 16.8789 2.23486 16.0479 2.23486C15.3665 2.23486 14.7826 2.35465 14.2959 2.59424C13.8167 2.82633 13.4049 3.12207 13.0605 3.48145C12.7161 3.83333 12.4167 4.18522 12.1621 4.53711C11.9974 4.75423 11.8551 4.90771 11.7354 4.99756C11.623 5.0874 11.492 5.13232 11.3423 5.13232C11.2 5.13232 11.069 5.09115 10.9492 5.00879C10.8294 4.91895 10.6872 4.76172 10.5225 4.53711C10.2904 4.17773 10.0021 3.8221 9.65771 3.47021C9.31331 3.11833 8.89404 2.82633 8.3999 2.59424C7.90576 2.35465 7.32178 2.23486 6.64795 2.23486C5.81689 2.23486 5.0682 2.43701 4.40186 2.84131C3.743 3.23812 3.22266 3.79215 2.84082 4.50342C2.45898 5.21468 2.26807 6.04574 2.26807 6.99658Z"
                      fill="#000000"
                    />
                    <path
                      d="M-0.000488281 7.00781C-0.000488281 5.97461 0.164225 5.03125 0.493652 4.17773C0.823079 3.31673 1.28353 2.57552 1.875 1.9541C2.47396 1.33268 3.17025 0.853516 3.96387 0.516602C4.75749 0.172201 5.611 0 6.52441 0C7.59505 0 8.54215 0.228353 9.36572 0.685059C10.1893 1.13428 10.8481 1.72949 11.3423 2.4707C11.8514 1.72949 12.514 1.13428 13.3301 0.685059C14.1536 0.228353 15.1007 0 16.1714 0C17.0923 0 17.9458 0.172201 18.7319 0.516602C19.5256 0.853516 20.2181 1.33268 20.8096 1.9541C21.401 2.57552 21.8615 3.31673 22.1909 4.17773C22.5278 5.03125 22.6963 5.97461 22.6963 7.00781C22.6963 8.60254 22.2733 10.186 21.4272 11.7583C20.5812 13.3306 19.387 14.8579 17.8447 16.3403C16.3099 17.8228 14.4943 19.2378 12.3979 20.5854C12.2257 20.6978 12.0423 20.7988 11.8477 20.8887C11.6605 20.9785 11.492 21.0234 11.3423 21.0234C11.2075 21.0234 11.0428 20.9785 10.8481 20.8887C10.6535 20.7988 10.4738 20.6978 10.3091 20.5854C8.21273 19.2378 6.38965 17.8228 4.83984 16.3403C3.29753 14.8579 2.10335 13.3306 1.25732 11.7583C0.418783 10.186 -0.000488281 8.60254 -0.000488281 7.00781ZM2.26807 6.99658C2.26807 7.98486 2.50391 8.99186 2.97559 10.0176C3.45475 11.0433 4.10612 12.0578 4.92969 13.061C5.75326 14.0643 6.69661 15.0264 7.75977 15.9473C8.8304 16.8607 9.95719 17.7104 11.1401 18.4966C11.245 18.5789 11.3123 18.6201 11.3423 18.6201C11.3722 18.6201 11.4471 18.5789 11.5669 18.4966C12.7498 17.7104 13.8729 16.8607 14.936 15.9473C15.9992 15.0264 16.9425 14.0643 17.7661 13.061C18.5897 12.0578 19.2373 11.0433 19.709 10.0176C20.1882 8.99186 20.4277 7.98486 20.4277 6.99658C20.4277 6.04574 20.2368 5.21468 19.855 4.50342C19.4731 3.79215 18.9491 3.23812 18.2827 2.84131C17.6239 2.43701 16.8789 2.23486 16.0479 2.23486C15.3665 2.23486 14.7826 2.35465 14.2959 2.59424C13.8167 2.82633 13.4049 3.12207 13.0605 3.48145C12.7161 3.83333 12.4167 4.18522 12.1621 4.53711C11.9974 4.75423 11.8551 4.90771 11.7354 4.99756C11.623 5.0874 11.492 5.13232 11.3423 5.13232C11.2 5.13232 11.069 5.09115 10.9492 5.00879C10.8294 4.91895 10.6872 4.76172 10.5225 4.53711C10.2904 4.17773 10.0021 3.8221 9.65771 3.47021C9.31331 3.11833 8.89404 2.82633 8.3999 2.59424C7.90576 2.35465 7.32178 2.23486 6.64795 2.23486C5.81689 2.23486 5.0682 2.43701 4.40186 2.84131C3.743 3.23812 3.22266 3.79215 2.84082 4.50342C2.45898 5.21468 2.26807 6.04574 2.26807 6.99658Z"
                      fill="#383181"
                      fill-opacity="0.5"
                    />
                  </svg>
                )}
              </span>
            </Button>
          </div>
        </div>
      </div>

      {/* Description Block */}
      {isMobile ? (
        <div className={`flex ${mobileStyles.descriptionBlock} h-max`}>
          <div className="flex-1  p-[15px] flex flex-col">
            <h2 className="font-[650] tracking-[-0.02em] text-[20px] text-[#232323]">
              О товаре
            </h2>
            <div className="mt-[20px] text-[15px] text-[#828282] tracking-[-0.02em]">
              <p>{productStore.product?.description}</p>
            </div>

            <ol className="mt-[30px] flex flex-col gap-[10px] text-[#474A51] text-[14px] font-semibold">
              <li className="flex flex-row">
                <span>Бренд</span>
                <div className="w-full border-b-[1px] border-[#828282] border-dotted ml-[3px] mr-[3px]"></div>
                <span className="text-nowrap whitespace-nowrap">
                  {productStore.product?.brand}
                </span>
              </li>
              <li className="flex flex-row">
                <span className="">Страна</span>
                <div className="w-full border-b-[1px] border-[#828282] border-dotted ml-[3px] mr-[3px]"></div>
                <span className="text-nowrap whitespace-nowrap">
                  {productStore.product?.country?.replace(/\s*\([^)]*\)/g, '').trim()}
                </span>
              </li>
            </ol>
          </div>
          <div className="flex-1 rounded-[20px] p-[15px]">
            <h2 className="font-[650] tracking-[-0.02em] text-[20px] text-[#232323]">
              Наличие в магазинах
            </h2>
            <span className="font-medium text-[14px] text-[#828282]">
              Показано наличие товара в магазинах города{" "}
              <span className="text-[#007AFF]">{appStore.selectedCity}</span>
            </span>
            <div className="flex flex-col mt-[30px]">
              {renderStoreAvailability()}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-row gap-[30px] h-max w-[1380px]">
          <div className="flex-1 bg-white shadow-lg [box-shadow:0_2px_20px_0_#0000001A] [-webkit-box-shadow:0_2px_20px_0_#0000001A] rounded-[20px] p-[30px] flex flex-col">
            <h2 className="font-[650] tracking-[-0.02em] text-[24px] text-[#232323]">
              О товаре
            </h2>
            <div className="mt-[15px] text-[17px] text-[#828282] tracking-[-0.02em]">
              <p>{productStore.product?.description}</p>
            </div>

            <ol className="mt-[30px] flex flex-col gap-[10px] text-[#474A51] text-[17px] font-semibold">
              <li className="flex flex-row">
                <span>Бренд</span>
                <div className="w-full border-b-[1px] border-[#828282] border-dotted"></div>
                <span className="text-nowrap whitespace-nowrap">
                  {productStore.product?.brand}
                </span>
              </li>
              <li className="flex flex-row">
                <span className="">Страна</span>
                <div className="w-full border-b-[1px] border-[#828282] border-dotted"></div>
                <span className="text-nowrap whitespace-nowrap">
                  {productStore.product?.country?.replace(/\s*\([^)]*\)/g, '').trim()}
                </span>
              </li>
            </ol>
          </div>
          <div className="flex-1 bg-white shadow-lg [box-shadow:0_2px_20px_0_#0000001A] [-webkit-box-shadow:0_2px_20px_0_#0000001A] rounded-[20px] p-[30px]">
            <h2 className="font-[650] tracking-[-0.02em] text-[24px] text-[#232323]">
              Наличие в магазинах
            </h2>
            <span className="font-medium text-[17px] text-[#828282]">
              Показано наличие товара в магазинах города{" "}
              <span className="text-[#007AFF]">{appStore.selectedCity}</span>
            </span>
            <div className="flex flex-col mt-[30px]">
              {renderStoreAvailability()}
            </div>
          </div>
        </div>
      )}

      {/* Analog Products */}
      {renderAnalogProducts()}

      {/* Add to Cart Modal */}
      <Modal
        isOpen={selectedBuy}
        onClose={onCloseModal}
        size={isMobile ? "md" : "xl"}
        closeButton={false}
        footer={
          <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-2`}>
            <Button style="outline" onClick={onCloseModal}>
              Назад
            </Button>
            <Button
              style="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={!watch("warehouse")}
            >
              В корзину
            </Button>
          </div>
        }
      >
        {!productStore.product ? (
          <Spinner />
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="p-4 flex flex-col min-h-[50vh] gap-4"
          >
            <h2 className="text-xl font-bold">Добавить в корзину</h2>
            <div className="flex flex-col gap-2">
              <label className="text-sm">
                Выберите точку получения в списке:
              </label>
              <Select
                options={
                  productStore?.availavleCurrentCity?.map((i) => ({
                    value: i.Stock,
                    label: i.Stock,
                  })) || []
                }
                value={watch("warehouse")}
                label={
                  <span className="font-semibold text-base">
                    Магазины в{" "}
                    <span className="text-[#007AFF]">
                      г. {appStore.selectedCity}
                    </span>
                  </span>
                }
                placeholder="Выберете магазин"
                onChange={(value) => {
                  setValue("warehouse", String(value));
                  setValue("quantity", 1);
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm">Количество:</label>
              <Counter
                min={1}
                max={100}
                value={watch("quantity")}
                onChange={handleQuantityChange}
              />
            </div>
          </form>
        )}
      </Modal>

      <ToastContainer position={isMobile ? "bottom-center" : "bottom-right"} />
    </div>
  );
};

export default observer(ProductPage);
